import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { useAuth } from '../../contexts/AuthContext';
import { farmAPI, invitesAPI } from '../../services/api';
import { UserGroupIcon, EnvelopeIcon, Cog6ToothIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const ROLE_ORDER = ['viewer','editor','manager','owner'];
const roleAtLeast = (r, min) => ROLE_ORDER.indexOf(r || 'viewer') >= ROLE_ORDER.indexOf(min);

const FarmSettings = () => {
  const { farmId } = useParams();
  const { currentFarm, getFarm } = useFarm();
  const { user } = useAuth();
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingRole, setEditingRole] = React.useState({});
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('viewer');
  const [inviteSending, setInviteSending] = React.useState(false);
  const [acceptLink, setAcceptLink] = React.useState('');

  // Compute role: prefer membership list (authoritative), fallback to farm.currentUserRole, else viewer
  const computedMember = React.useMemo(() => {
    return members.find(m => m.user?.id === user?.id || (m.user?.email && m.user.email.toLowerCase() === (user?.email || '').toLowerCase()));
  }, [members, user]);
  const myRole = computedMember?.role || currentFarm?.currentUserRole || 'viewer';
  const canManageMembers = roleAtLeast(myRole, 'manager');

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await getFarm(farmId);
        const res = await farmAPI.listMembers(farmId);
        setMembers(res.data?.members || []);
      } catch (e) {} finally {
        setLoading(false);
      }
    })();
  }, [farmId]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!canManageMembers) return;
    try {
      setInviteSending(true);
      setAcceptLink('');
      const res = await invitesAPI.create(farmId, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      setInviteRole('viewer');
      setAcceptLink(res.data?.acceptUrl || '');
      // Soft-refresh members (invited users are not active yet)
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const refreshMembers = async () => {
    const res = await farmAPI.listMembers(farmId);
    setMembers(res.data?.members || []);
  };

  const saveRole = async (memberId) => {
    try {
      const newRole = editingRole[memberId];
      if (!newRole) return;
      await farmAPI.updateMemberRole(farmId, memberId, newRole);
      await refreshMembers();
      setEditingRole(prev => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to update role');
    }
  };

  const removeMember = async (member) => {
    try {
      if (!canManageMembers) return;
      if (member.role === 'owner') return;
      if (member.user?.id === user?.id) {
        alert('You cannot remove yourself.');
        return;
      }
      const ok = window.confirm(`Remove ${member.user?.email || 'this member'} from the farm?`);
      if (!ok) return;
      await farmAPI.removeMember(farmId, member.id);
      await refreshMembers();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <Cog6ToothIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Farm Settings</h1>
              <p className="text-green-100">Manage access and members for {currentFarm?.name || 'your farm'}</p>
            </div>
          </div>
          <Link 
            to={`/farm/${farmId}`} 
            className="inline-flex items-center px-5 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-medium"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Farm
          </Link>
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-2 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{members.length}</p>
            <p className="text-green-200 text-xs">Team Members</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold capitalize">{myRole}</p>
            <p className="text-green-200 text-xs">Your Role</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-4 sm:px-6">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            <span className="border-green-500 text-green-700 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center">
              <UserGroupIcon className="h-4 w-4 mr-1.5" /> Members
            </span>
          </nav>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-gray-600">Loading members...</div>
          ) : (
            <>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Current Members</h2>
                <p className="text-sm text-gray-600">You are a <span className="font-medium">{myRole}</span> on this farm.</p>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Role</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {members.map((m) => (
                      <tr key={m.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{m.user?.firstName || ''} {m.user?.lastName || ''}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{m.user?.email}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {canManageMembers && m.role !== 'owner' ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="input !py-1 !px-2 text-xs"
                                value={editingRole[m.id] ?? m.role}
                                onChange={(e)=> setEditingRole(prev => ({ ...prev, [m.id]: e.target.value }))}
                              >
                                <option value="viewer">Viewer</option>
                                <option value="editor">Editor</option>
                                <option value="manager">Manager</option>
                              </select>
                              <button className="text-xs border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50" onClick={()=>saveRole(m.id)}>
                                Save
                              </button>
                            </div>
                          ) : (
                            <span className="capitalize">{m.role}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {canManageMembers && m.role !== 'owner' ? (
                            <button
                              className="text-xs text-red-600 border border-red-300 rounded-md px-3 py-1 hover:bg-red-50"
                              onClick={()=>removeMember(m)}
                            >
                              Remove
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                    {members.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">No members yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-8">
                <h3 className="text-md font-semibold text-gray-900 mb-2">Invite a member</h3>
                {!canManageMembers && (
                  <div className="text-sm text-gray-500 mb-2">Only managers or owners can invite members.</div>
                )}
                <form className="grid grid-cols-1 md:grid-cols-3 gap-3" onSubmit={sendInvite}>
                  <input
                    type="email"
                    className="input"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e)=>setInviteEmail(e.target.value)}
                    disabled={!canManageMembers}
                    required
                  />
                  <select className="input" value={inviteRole} onChange={(e)=>setInviteRole(e.target.value)} disabled={!canManageMembers}>
                    <option value="viewer">Viewer (read-only)</option>
                    <option value="editor">Editor (add/update records)</option>
                    <option value="manager">Manager (manage members and settings)</option>
                  </select>
                  <button type="submit" className="btn-primary inline-flex items-center justify-center" disabled={!canManageMembers || inviteSending}>
                    <EnvelopeIcon className="h-4 w-4 mr-1"/> {inviteSending ? 'Sending...' : 'Send Invite'}
                  </button>
                </form>
                {acceptLink && (
                  <div className="mt-3 text-xs text-gray-600">
                    Email sent. If email isn’t configured, share this link manually:
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded break-all">{acceptLink}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FarmSettings;


